import { DataTable } from "../DataTable";
import dayjs from "dayjs";
import {
  useExportParticipants,
  useGetGiveawayParticipants,
} from "../../api/giveaways";
import {
  Avatar,
  Box,
  Group,
  Progress,
  Select,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import defaultSponsorPlaceholder from "../../assets/defaultSponsorProfile.png";
import { IconDownload } from "@tabler/icons-react";
import { useSetActionButtons } from "../../services/ActionButtonsContext";
import { openExportModal } from "./ExportModal";
import GiveawayOverview from "./Overview";

const Participants = ({ giveaway, sponsors }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState({
    columnAccessor: "lastScanAt",
    direction: "asc",
  });
  const [page, setPage] = useState(1);

  const { mutate: exportParticipants } = useExportParticipants();

  const statusFilter = searchParams.get("status")
    ? searchParams.get("status")
    : "";

  const actions = useMemo(
    () => [
      {
        label: "Export",
        icon: <IconDownload />,
        onClick: () => {
          openExportModal(giveaway.id, exportParticipants);
        },
      },
    ],
    [giveaway.id, exportParticipants]
  );

  useSetActionButtons(actions);

  const handleChangeProgress = (status) => {
    setSearchParams((searchParams) => {
      if (!status) {
        searchParams.delete("status");
        return searchParams;
      }
      searchParams.set("status", status);
      return searchParams;
    });
  };

  const { data: participants, isPending } = useGetGiveawayParticipants({
    id: giveaway?.id,
    statusFilter,
    sort,
    page,
    limit: 10,
  });

  const getScannedSponsors = (scannedSponsorIds) => {
    return sponsors?.data.filter((sponsor) =>
      scannedSponsorIds.includes(sponsor.id)
    );
  };

  return (
    <>
      <GiveawayOverview giveawayId={giveaway?.id} />
      <DataTable
        borderRadius="md"
        records={participants?.data}
        page={page}
        onPageChange={setPage}
        recordsPerPage={participants?.pagination?.perPage}
        totalRecords={participants?.pagination.total}
        fetching={isPending}
        sortStatus={sort}
        onSortStatusChange={setSort}
        columns={[
          { accessor: "firstName", title: "First Name", sortable: true },
          { accessor: "lastName", title: "Last Name", sortable: true },
          { accessor: "email", title: "Email", sortable: true },
          {
            accessor: "progress",
            title: "Progress",
            sortable: true,
            filtering: !!statusFilter,
            filter: (
              <Select
                label="Progress"
                placeholder="Filter progress status"
                value={statusFilter}
                data={[
                  {
                    value: "in-progress",
                    label: "In progress",
                  },
                  { value: "completed", label: "Completed" },
                ]}
                onChange={(value) => {
                  handleChangeProgress(value);
                }}
                clearable
              />
            ),
            render: (row) => {
              if (row.isCompleted) {
                return (
                  <Progress.Root size={10}>
                    <Tooltip label="Completed">
                      <Progress.Section
                        value={100}
                        color="green"
                      ></Progress.Section>
                    </Tooltip>
                  </Progress.Root>
                );
              }
              return (
                <Progress.Root size={10}>
                  <Tooltip
                    label={`In progress - ${row.progress} / ${giveaway?.sponsorIds?.length}`}
                  >
                    <Progress.Section
                      value={
                        (row.progress / giveaway?.sponsorIds?.length) * 100
                      }
                      color="violet"
                    ></Progress.Section>
                  </Tooltip>
                </Progress.Root>
              );
            },
          },

          {
            accessor: "lastScanAt",
            title: "Last Scan",
            sortable: true,
            render: (row) =>
              row.lastScanAt &&
              dayjs(row.lastScanAt).format("YYYY-MM-DD HH:mm:ss"),
          },
        ]}
        rowExpansion={{
          content: ({ record }) => {
            const scannedSponsors = getScannedSponsors(
              record.scannedSponsorIds
            );
            return (
              <Box
                p="md"
                pl="lg"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Text size="sm" mb="xs" style={{ fontWeight: "bold" }}>
                  Scanned sponsors:
                </Text>
                <Group wrap="wrap">
                  {scannedSponsors.map((sponsor) => (
                    <Group
                      gap="md"
                      align="center"
                      mb={10}
                      key={sponsor.id}
                      wrap="no-wrap"
                      mr={30}
                    >
                      <Avatar
                        src={sponsor.logo || defaultSponsorPlaceholder}
                        size={50}
                      />
                      <Box style={{ flex: 1 }} maw={500}>
                        <Title order={6} lineClamp={2}>
                          {sponsor.name}
                        </Title>
                      </Box>
                    </Group>
                  ))}
                </Group>
              </Box>
            );
          },
        }}
      />
    </>
  );
};

export default Participants;
